import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Resource {
  id: string
  name: string
  type: string
  capacity: number
  utilized: number
  utilization_pct: number
  trend: string
  forecast_need: number
}

interface CapacityForecast {
  period: string
  demand_forecast: number
  current_capacity: number
  gap: number
  recommendation: string
}

interface Statistics {
  avg_utilization: number
  resources_at_capacity: number
  expansion_needed: number
  forecast_accuracy: number
  bottleneck_count: number
  efficiency_score: number
}

export default function CapacityPlanningPage() {
  const [resources, setResources] = useState<Resource[]>([])
  const [forecast, setForecast] = useState<CapacityForecast[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [resourcesRes, forecastRes, statsRes] = await Promise.all([
        fetch('/api/capacityplanning/resources'),
        fetch('/api/capacityplanning/forecast'),
        fetch('/api/capacityplanning/statistics')
      ])
      if (resourcesRes.ok) setResources(await resourcesRes.json())
      if (forecastRes.ok) setForecast(await forecastRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching capacity planning data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  const getRecommendationBadge = (rec: string) => {
    if (rec === 'Urgent Expansion') return 'bg-red-100 text-red-800'
    if (rec === 'Expand') return 'bg-orange-100 text-orange-800'
    if (rec === 'Monitor') return 'bg-yellow-100 text-yellow-800'
    return 'bg-green-100 text-green-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Capacity Planning</h1>
          <p className="mt-2 text-gray-600">Phase 260 - Resource capacity and forecasting</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Utilization</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.avg_utilization}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">At Capacity</p>
              <p className="text-2xl font-bold text-red-600">{statistics.resources_at_capacity}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Expansion Needed</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.expansion_needed}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Forecast Accuracy</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.forecast_accuracy}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Bottlenecks</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.bottleneck_count}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Efficiency</p>
              <p className="text-2xl font-bold text-green-600">{statistics.efficiency_score}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Resource Capacity</h2>
          </div>
          <div className="p-6 space-y-4">
            {resources.map((resource) => (
              <div key={resource.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="font-medium text-gray-900">{resource.name}</span>
                    <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">{resource.type}</span>
                  </div>
                  <span className={`text-lg font-bold ${resource.utilization_pct >= 90 ? 'text-red-600' : resource.utilization_pct >= 75 ? 'text-yellow-600' : 'text-green-600'}`}>{resource.utilization_pct}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full mb-3">
                  <div className={`h-2 rounded-full ${resource.utilization_pct >= 90 ? 'bg-red-500' : resource.utilization_pct >= 75 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{width: `${resource.utilization_pct}%`}}></div>
                </div>
                <div className="grid grid-cols-4 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Capacity</p>
                    <p className="font-medium text-blue-600">{formatNumber(resource.capacity)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Utilized</p>
                    <p className="font-medium text-purple-600">{formatNumber(resource.utilized)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Trend</p>
                    <p className={`font-medium ${resource.trend === 'up' ? 'text-orange-600' : 'text-green-600'}`}>{resource.trend}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Forecast Need</p>
                    <p className="font-medium text-gray-900">{formatNumber(resource.forecast_need)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Capacity Forecast</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Demand Forecast</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Capacity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gap</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recommendation</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {forecast.map((period, idx) => (
                  <tr key={idx}>
                    <td className="px-6 py-4 font-medium text-gray-900">{period.period}</td>
                    <td className="px-6 py-4 text-blue-600">{formatNumber(period.demand_forecast)}</td>
                    <td className="px-6 py-4 text-gray-500">{formatNumber(period.current_capacity)}</td>
                    <td className={`px-6 py-4 font-bold ${period.gap >= 0 ? 'text-green-600' : 'text-red-600'}`}>{period.gap >= 0 ? '+' : ''}{formatNumber(period.gap)}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getRecommendationBadge(period.recommendation)}`}>{period.recommendation}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
